import { isArray, isBoolean, isPlainObject, isString } from 'lodash'

import type { Quota } from '~/data/api/client/assistant'
import type { AssistantUIMessage } from '~/data/api/client/assistant/enhancedEndpoints'
import type { AssistantDashboardPanel } from '~/features/assistant/streamTypes'
import parseBillingDraft from '~/features/billing-composer/parseBillingDraft'
import type { BillingDraft } from '~/features/billing-composer/types'

/**
 * The custom data parts assistant-api writes into the UI message stream, and the only module that knows their
 * wire names and shapes. The stream is not schema-validated on the way in, so every reader checks the shape
 * and returns `undefined` for a part that is not the one it reads or does not match, and renderers drop it.
 *
 * Further kinds of data part (skills) get a name and a reader here and a branch in `AssistantTurn`.
 */
const ASSISTANT_DATA_PART_TYPES = {
  /** A mutation the assistant proposes; it runs only once a person approves it. The part id is the action id. */
  proposedAction: 'data-proposed-action',
  /**
   * Questions the assistant needs answered, sent once right after the clarification tool's output; the part id is
   * that tool call's id. The turn ends after it and the answers go back as the next user message.
   */
  clarification: 'data-clarification',
  /** Transient: the organization's message allowance after the turn, delivered to `onData` and never stored. */
  quota: 'data-quota',
  /**
   * The dashboard a turn builds, the part id being the dashboard's. Every change re-sends the whole dashboard
   * under that id, so the part is replaced in place: tiles arrive as `pending` placeholders and turn `ready` as
   * their data lands. The stored message carries the finished dashboard.
   */
  dashboard: 'data-dashboard',
  /**
   * The file a finished long-running action produced, in the message assistant-api posts when the action ends. It
   * names the action and the file, never a URL: download links are presigned and short-lived.
   */
  artifactLink: 'data-artifact-link',
  /**
   * A billing product or agreement draft from `submit_billing_draft`, with its tax guidance and completeness; the
   * part id is the draft id. The stored message carries it too, so a reopened conversation shows the same card.
   */
  billingDraft: 'data-billing-draft'
} as const

/** The `url` scheme of a file part that references an attachment uploaded to the conversation. */
const ATTACHMENT_URL_PREFIX = 'attachment:'

export type AssistantMessagePart = AssistantUIMessage['parts'][number]

export type ActionProposal = {
  actionId: string
  actionType: string
  longRunning: boolean
  /** The approval-card text: what the action will change and why. */
  reason: string
  /** The slice of the action's input the approver sees. */
  preview: Record<string, unknown>
}

type ClarificationQuestionType = 'time_range' | 'single' | 'multi' | 'scope'

type ClarificationOption = {
  id: string
  label: string
  /** The answer text sent back; the label when absent. */
  value: string
}

export type ClarificationQuestion = {
  id: string
  prompt: string
  type: ClarificationQuestionType
  options: ClarificationOption[]
}

export type Clarification = {
  intro?: string
  questions: ClarificationQuestion[]
}

const QUESTION_TYPES: readonly ClarificationQuestionType[] = ['time_range', 'single', 'multi', 'scope']

export type Dashboard = {
  dashboardId: string
  /** In tile order; the renderer shapes each by its `panelType`. */
  panels: AssistantDashboardPanel[]
}

/** A file the operator attached to a message. */
export type MessageAttachment = {
  attachmentId: string
  filename: string
  mediaType: string
}

export function readActionProposal(part: AssistantMessagePart): ActionProposal | undefined {
  const data = dataOf(part, ASSISTANT_DATA_PART_TYPES.proposedAction)
  if (!data || !isString(data.actionId) || !isString(data.reason)) return undefined
  return {
    actionId: data.actionId,
    actionType: isString(data.actionType) ? data.actionType : '',
    longRunning: isBoolean(data.longRunning) ? data.longRunning : false,
    reason: data.reason,
    preview: isRecord(data.preview) ? data.preview : {}
  }
}

export function readClarification(part: AssistantMessagePart): Clarification | undefined {
  const data = dataOf(part, ASSISTANT_DATA_PART_TYPES.clarification)
  if (!data || !isArray(data.questions)) return undefined
  const questions = data.questions.flatMap(readQuestion)
  if (questions.length === 0) return undefined
  return { ...(isString(data.intro) ? { intro: data.intro } : {}), questions }
}

export function readQuota(part: AssistantMessagePart): Quota | undefined {
  const data = dataOf(part, ASSISTANT_DATA_PART_TYPES.quota)
  if (!data || !isFiniteNumber(data.used) || !isFiniteNumber(data.limit) || !isFiniteNumber(data.remaining)) {
    return undefined
  }
  return { used: data.used, limit: data.limit, remaining: data.remaining }
}

export type ArtifactLinkPart = {
  actionId: string
  filename: string
}

export function readArtifactLink(part: AssistantMessagePart): ArtifactLinkPart | undefined {
  const data = dataOf(part, ASSISTANT_DATA_PART_TYPES.artifactLink)
  if (!data || !isString(data.actionId) || !isString(data.filename)) return undefined
  return { actionId: data.actionId, filename: data.filename }
}

export function readDashboard(part: AssistantMessagePart): Dashboard | undefined {
  const data = dataOf(part, ASSISTANT_DATA_PART_TYPES.dashboard)
  if (!data || !isString(data.dashboardId) || !isArray(data.panels)) return undefined
  return { dashboardId: data.dashboardId, panels: data.panels.flatMap(readPanel) }
}

export function readBillingDraft(part: AssistantMessagePart): BillingDraft | undefined {
  const data = dataOf(part, ASSISTANT_DATA_PART_TYPES.billingDraft)
  if (!data) return undefined
  const partId = 'id' in part && isString(part.id) ? part.id : ''
  return parseBillingDraft(data, partId) ?? undefined
}

/** The `url` of the file part that references the uploaded attachment `attachmentId`. */
export function attachmentUrl(attachmentId: string): string {
  return `${ATTACHMENT_URL_PREFIX}${attachmentId}`
}

/** The attachment a user message's file part references, or `undefined` for any other part. */
export function readAttachment(part: AssistantMessagePart): MessageAttachment | undefined {
  if (part.type !== 'file' || !part.url.startsWith(ATTACHMENT_URL_PREFIX)) return undefined
  const attachmentId = part.url.slice(ATTACHMENT_URL_PREFIX.length)
  if (!attachmentId) return undefined
  return { attachmentId, filename: part.filename ?? attachmentId, mediaType: part.mediaType }
}

/** The rating the viewer gave an assistant message, which the stored transcript carries in its metadata. */
export function readFeedbackRating(message: AssistantUIMessage): 'up' | 'down' | undefined {
  const rating = message.metadata?.feedbackRating
  return rating === 'up' || rating === 'down' ? rating : undefined
}

function readPanel(panel: unknown): AssistantDashboardPanel[] {
  if (!isRecord(panel) || !isString(panel.panelId) || !isString(panel.panelType)) return []
  return [
    {
      panelId: panel.panelId,
      panelType: panel.panelType,
      panelConfig: isRecord(panel.panelConfig) ? panel.panelConfig : {},
      provenance: panel.provenance,
      status: panel.status === 'pending' ? 'pending' : 'ready',
      ...(isString(panel.title) ? { title: panel.title } : {}),
      ...(isString(panel.summaryText) ? { summaryText: panel.summaryText } : {}),
      ...(isString(panel.tileId) ? { tileId: panel.tileId } : {})
    }
  ]
}

function readQuestion(question: unknown): ClarificationQuestion[] {
  if (!isRecord(question) || !isString(question.id) || !isString(question.prompt)) return []
  if (!isQuestionType(question.type)) return []
  const options = isArray(question.options) ? question.options.flatMap(readOption) : []
  return [{ id: question.id, prompt: question.prompt, type: question.type, options }]
}

function isQuestionType(value: unknown): value is ClarificationQuestionType {
  return QUESTION_TYPES.some((type) => type === value)
}

function readOption(option: unknown, index: number): ClarificationOption[] {
  if (!isRecord(option)) return []
  const label = isString(option.label) ? option.label : isString(option.value) ? option.value : undefined
  if (!label) return []
  return [
    {
      id: isString(option.id) ? option.id : String(index),
      label,
      value: isString(option.value) ? option.value : label
    }
  ]
}

function dataOf(part: AssistantMessagePart, type: string): Record<string, unknown> | undefined {
  if (part.type !== type || !('data' in part) || !isRecord(part.data)) return undefined
  return part.data
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return isPlainObject(value)
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}
